// src/pages/GiftCardReceiverPage.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from '../context/AuthContext';
import { convertToPersianNumbers } from '../utils/PersianNumberUtils';

const GiftCardReceiverPage = () => {
  const { uuid } = useParams();
  const { isLoggedIn, openAuthModal } = useAuthContext();
  const [giftCard, setGiftCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');

  const getVariantFromUuid = (uuid) => {
    if (!uuid) return 1;
    const hash = uuid.split('-')[0];
    let sum = 0;
    for (let i = 0; i < hash.length; i++) sum += hash.charCodeAt(i);
    return (sum % 4) + 1;
  };

  const getCardColor = (imageUrl, uuid) => {
    if (imageUrl && imageUrl !== '') {
      const match = imageUrl.match(/(\d)-\d\.png/);
      const num = match ? parseInt(match[1]) : 0;
      if (num >= 1 && num <= 4) {
        return ['oklch(44.8% 0.119 151.328)', '#293038', 'oklch(68.5% 0.169 237.323)', 'oklch(79.5% 0.184 86.047)'][num - 1];
      }
    }
    const variant = getVariantFromUuid(uuid);
    return ['oklch(44.8% 0.119 151.328)', '#293038', 'oklch(68.5% 0.169 237.323)', 'oklch(79.5% 0.184 86.047)'][variant - 1];
  };

  const getImageUrl = (imageUrl, uuid) => {
    let baseUrl = '';
    if (imageUrl && imageUrl.trim() !== '') {
      baseUrl = imageUrl;
    } else {
      const variant = getVariantFromUuid(uuid);
      baseUrl = `https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/giftcard/${variant}-1.png`;
    }
    const timestamp = Date.now();
    return `${baseUrl}?v=${timestamp}`;
  };

  useEffect(() => {
    const fetchGiftCard = async () => {
      try {
        const response = await fetch(`https://api.farzaneh.app/api/v1/wallet/public-giftcard/${uuid}/`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setGiftCard(data);
      } catch (err) {
        console.error(err);
        setError('کارت هدیه معتبر نیست یا قبلاً استفاده شده است.');
      } finally {
        setLoading(false);
      }
    };
    if (uuid) fetchGiftCard();
  }, [uuid]);

  const copyCodeToClipboard = async () => {
    if (!giftCard?.code) return;
    try {
      await navigator.clipboard.writeText(giftCard.code);
      setCopyMessage('✅ کد با موفقیت کپی شد!');
      setTimeout(() => setCopyMessage(''), 3000);
    } catch (err) {
      console.error('خطا در کپی:', err);
      setCopyMessage('❌ خطا در کپی کد');
      setTimeout(() => setCopyMessage(''), 3000);
    }
  };

  if (loading) return <div className="text-center p-10 text-white">در حال بارگذاری...</div>;
  if (error) return <div className="text-center text-red-500 p-10">{error}</div>;

  const finalImageUrl = getImageUrl(giftCard.image_url, giftCard.share_uuid);
  const bgColor = getCardColor(giftCard.image_url, giftCard.share_uuid);
  const formattedAmount = convertToPersianNumbers(parseFloat(giftCard.amount).toLocaleString());

  // متا تگ‌ها
  const metaTitle = `کارت هدیه ${formattedAmount} تومانی | فرزانه`;
  const metaDescription = giftCard.message
    ? `هدیه‌ای به ارزش ${formattedAmount} تومان از طرف ${giftCard.sender_name || 'یکی از کاربران'} با پیام: «${giftCard.message}»`
    : `کارت هدیه ${formattedAmount} تومانی از طرف ${giftCard.sender_name || 'فرزانه'}. کد: ${giftCard.code}`;
  const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
  const ogImage = !imgError && finalImageUrl ? finalImageUrl : defaultOgImage;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        {/* فقط فونت وزیر برای کد و لینک */}
        <link
          href="https://cdn.jsdelivr.net/gh/rastikerdar/vazir-font@v30.1.0/dist/font-face.css"
          rel="stylesheet"
        />
      </Helmet>

      <style>{`
        .vazir-font {
          font-family: 'Vazir', sans-serif;
        }
      `}</style>

      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
        <div className="w-[80%] md:w-1/2">
          <div className="w-full mb-6 rounded-2xl overflow-hidden shadow-2xl bg-white/10">
            {!imgError ? (
              <img
                key={finalImageUrl}
                src={finalImageUrl}
                alt="کارت هدیه"
                className="w-full h-auto"
                onError={() => setImgError(true)}
                onLoad={() => console.log('تصویر لود شد')}
                crossOrigin="anonymous"
              />
            ) : (
              <div className="w-full h-48 flex items-center justify-center text-white text-center p-4 bg-black/30">
                تصویر کارت هدیه در دسترس نیست
              </div>
            )}
          </div>

          {/* تمام متن‌ها با فونت فعلی (پیش‌فرض) */}
          <div className="text-center text-white">
            <p className="text-[14] font-thin mb-2">کــارت هــدیــه فـرزانـه</p>
            <p className="text-2xl font-black mb-4">{formattedAmount} تومان</p>

            {/* پیام کاربر با همان فونت فعلی و وزن بولد */}
            {giftCard.message && (
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 mb-4 text-[10pt] font-bold">
                "{giftCard.message}"
              </div>
            )}

            {/* کد هدیه با فونت وزیر */}
            <div className="bg-white/10 rounded-lg p-2 text-sm font-mono tracking-widest mb-6 vazir-font">
              کد: {giftCard.code}
            </div>

            <button
              onClick={copyCodeToClipboard}
              className="w-full bg-white text-gray-800 font-bold py-3 rounded-xl transition hover:bg-opacity-90"
              style={{ color: bgColor }}
            >
              📋 کپی کد هدیه
            </button>
            {copyMessage && (
              <p className="text-xs mt-2 text-white/90">{copyMessage}</p>
            )}

            <div className="mt-6 text-xs text-white/80 text-center space-y-1">
              <p>برای فعال‌سازی کارت هدیه کافی است این کد را در لینک زیر کپی کنید:</p>
              {/* لینک با فونت وزیر */}
              <Link
                to="/profile/wallet"
                className="text-white underline hover:text-gray-200 break-all vazir-font"
                target="_blank"
                rel="noopener noreferrer"
              >
                http://farzaneh.app/profile/wallet
              </Link>
            </div>

            {!isLoggedIn && (
              <p className="text-xs text-white/70 mt-4">
                برای استفاده از کد هدیه، ابتدا وارد حساب خود شوید.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GiftCardReceiverPage;